/*
 * Project: bellcloud
 * File: components/ui/common/TestSummary.tsx
 * Description: Test summary component displaying calibration/bump test statistics
 */

import React from 'react';
import styled from '@emotion/styled';

interface TestSummaryData {
  total: number;
  next30Days: number;
  overDue: number;
}

interface TestSummaryProps {
  title: string;
  data: TestSummaryData;
  className?: string;
  onClick?: () => void;
}

const Container = styled.div<{ clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]}px;
  padding: ${({ theme }) => theme.spacing[6]}px;
  border-radius: ${({ theme }) => theme.borderRadius.lg}px;
  border: 1px solid ${({ theme }) => theme.colors.border.container};
  flex: 1;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
  ${({ clickable, theme }) =>
    clickable &&
    `&:hover {
      border-color: ${theme.colors.border.interactive.hover};
      background-color: ${theme.colors.surface.interactive.hover};
    }`}
`;

const TestTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.lg}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: 130%;
  margin: 0;
`;

const SummaryGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const SummaryBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[1]}px;
  flex: 1 0 0;
`;

const BlockLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.md}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: 130%;
`;

const BlockValueGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]}px;
`;

const ValueContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
`;

const ValueText = styled.span<{ variant?: 'default' | 'danger' | 'caution' }>`
  color: ${({ theme, variant = 'default' }) => {
    switch (variant) {
      case 'danger':
        return theme.colors.status.danger.stroke.text;
      case 'caution':
        return theme.colors.status.caution.stroke.text;
      default:
        return theme.colors.text.primary;
    }
  }};
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.xl}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: 130%;
`;

const IconContainer = styled.div<{ variant: 'danger' | 'caution' }>`
  width: 20px;
  height: 20px;
  border-radius: ${({ theme, variant }) =>
    variant === 'danger' ? theme.borderRadius.sm : theme.borderRadius.full}px;
  background-color: ${({ theme, variant }) =>
    variant === 'danger'
      ? theme.colors.status.danger.fill.surface
      : theme.colors.status.caution.fill.surface};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  ${({ variant }) =>
    variant === 'danger' &&
    `
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  `}
`;

const IconSymbol = styled.span<{ variant: 'danger' | 'caution' }>`
  color: ${({ theme }) => theme.colors.icon.inverse};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: 1;
  ${({ variant }) =>
    variant === 'danger' &&
    `
    margin-top: 4px;
  `}
`;

const PercentageChip = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing[1]}px ${({ theme }) => theme.spacing[4]}px;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  border-radius: ${({ theme }) => theme.borderRadius.full}px;
  border: 1px solid ${({ theme }) => theme.colors.icon.secondary};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: 130%;
`;

export const TestSummary: React.FC<TestSummaryProps> = ({ title, data, className, onClick }) => {
  const overDuePercentage = data.total > 0 ? ((data.overDue / data.total) * 100).toFixed(1) : '0.0';
  const next30DaysPercentage =
    data.total > 0 ? ((data.next30Days / data.total) * 100).toFixed(1) : '0.0';

  return (
    <Container className={className} clickable={!!onClick} onClick={onClick}>
      <TestTitle>{title}</TestTitle>
      <SummaryGroup>
        {/* Current (Overdue) */}
        <SummaryBlock>
          <BlockLabel>Current</BlockLabel>
          <BlockValueGroup>
            <ValueContainer>
              {data.overDue > 0 && (
                <IconContainer variant="caution">
                  <IconSymbol variant="caution">!</IconSymbol>
                </IconContainer>
              )}
              <ValueText variant={data.overDue > 0 ? 'caution' : 'default'}>
                {String(data.overDue).padStart(2, '0')}
              </ValueText>
            </ValueContainer>
            <PercentageChip>{overDuePercentage}%</PercentageChip>
          </BlockValueGroup>
        </SummaryBlock>

        {/* Upcoming in 30 Days */}
        <SummaryBlock>
          <BlockLabel>Due in 30 Days</BlockLabel>
          <BlockValueGroup>
            <ValueContainer>
              <ValueText>{String(data.next30Days).padStart(2, '0')}</ValueText>
            </ValueContainer>
            <PercentageChip>{next30DaysPercentage}%</PercentageChip>
          </BlockValueGroup>
        </SummaryBlock>

        {/* Total */}
        <SummaryBlock>
          <BlockLabel>Total</BlockLabel>
          <BlockValueGroup>
            <ValueContainer>
              <ValueText>{data.total}</ValueText>
            </ValueContainer>
          </BlockValueGroup>
        </SummaryBlock>
      </SummaryGroup>
    </Container>
  );
};
